import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { useMedia } from 'react-use';
import { CloseIconBigger } from 'static';
import email from 'static/animations/email-sent.json';
import errorCross from 'static/animations/error-cross.json';
import info from 'static/animations/info.json';
import success from 'static/animations/success-check.json';
import warningSignMotion from 'static/animations/warning-motion.json';
import warningSign from 'static/animations/warning-sign.json';
import { breakpoints } from 'theme';

import { Button } from '../../index';
import PopupWrapper from '../Wrapper';
import {
  CloseIconContainer,
  ContentWrapper,
  HeaderIcon,
  HeaderTitle,
  MainTitle,
  StyledButton,
  StyledButtonContainer,
  StyledButtonText,
  StyledHeaderContainer,
  StyledMessage,
  StyledMessageContainer,
  StyledPopupContainer,
  TwoButtonContainer,
} from './styled';

export interface PopupProps {
  isOpen?: boolean;
  headerText: string;
  messageText?: string;
  boldMessageText?: string;
  mainTitle?: string;
  defaultButtonText?: string;
  cancelButtonText?: string;
  okButtonText?: string;
  imageUrl?: string;
  twoButtons?: boolean;
  shortHeight?: boolean;
  shortWidth?: boolean;
  withoutCloseButton?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  type: 'success' | 'error' | 'warning' | 'email' | 'warningTwo';
  children?: JSX.Element;
}

const InfoPopupV2: React.FC<PopupProps> = ({
  isOpen = false,
  type,
  headerText,
  messageText,
  boldMessageText,
  mainTitle,
  defaultButtonText,
  cancelButtonText,
  okButtonText,
  twoButtons,
  shortHeight,
  shortWidth,
  withoutCloseButton = false,
  onClose = () => {},
  onConfirm,
  children,
}) => {
  const { t } = useTranslation();
  const isMobileView = useMedia(breakpoints.downSmPlus);
  const [open, setOpen] = useState<boolean>(isOpen || false);
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const animationOptions = useMemo(() => {
    let animationData;
    switch (type) {
      case 'success':
        animationData = success;
        break;
      case 'error':
        animationData = errorCross;
        break;
      case 'warning':
        animationData = warningSign;
        break;
      case 'warningTwo':
        animationData = warningSignMotion;
        break;
      case 'email':
        animationData = email;
        break;
      default:
        animationData = info;
        break;
    }
    return {
      animationData,
      loop: true,
      autoPlay: true,
    };
  }, [type]);

  const toggleOpen = () => {
    if (!isOpen && !open) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    open && setOpen(false);
    isOpen && onClose();
  };

  const handleConfirm = async () => {
    onConfirm && (await onConfirm());
    open && setOpen(false);
    isOpen && onClose();
  };

  return (
    <>
      {children && <div onClick={toggleOpen}>{children}</div>}
      <PopupWrapper isOpen={open}>
        <StyledPopupContainer
          isMobileView={isMobileView}
          className="react-toast-notifications__container"
        >
          {!withoutCloseButton && (
            <CloseIconContainer onClick={handleClose}>
              <CloseIconBigger />
            </CloseIconContainer>
          )}
          <ContentWrapper>
            <StyledHeaderContainer isMobileView={isMobileView}>
              {!shortHeight && (
                <HeaderIcon>
                  <Lottie
                    options={animationOptions}
                    width={isMobileView ? '80px' : '150px'}
                    height={isMobileView ? '80px' : '150px'}
                  />
                </HeaderIcon>
              )}
              {mainTitle && <MainTitle>{mainTitle}</MainTitle>}
              <HeaderTitle>
                {shortHeight && (
                  <Lottie
                    options={animationOptions}
                    width="40px"
                    height="40px"
                  />
                )}
                {headerText}
              </HeaderTitle>
              <StyledMessageContainer $overflowY="hidden">
                <StyledMessage>{messageText}</StyledMessage>
                {boldMessageText && (
                  <StyledMessage
                    textBold={!!boldMessageText}
                    $overflowY="hidden"
                  >
                    {boldMessageText}
                  </StyledMessage>
                )}
              </StyledMessageContainer>
            </StyledHeaderContainer>
            <StyledButtonContainer>
              {twoButtons ? (
                <TwoButtonContainer>
                  <Button
                    onClick={handleClose}
                    width={isMobileView ? '100%' : 'auto'}
                    variant="secondary"
                  >
                    {cancelButtonText}
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    width={isMobileView ? '100%' : '240px'}
                    variant="default"
                  >
                    {okButtonText}
                  </Button>
                </TwoButtonContainer>
              ) : (
                <Button type="button" onClick={handleConfirm}>
                  {defaultButtonText ?? t('common.ok')}
                </Button>
              )}
            </StyledButtonContainer>
          </ContentWrapper>
        </StyledPopupContainer>
      </PopupWrapper>
    </>
  );
};

export default InfoPopupV2;
